import React, { useState, useEffect, useCallback, useRef } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import ImageUploadField from '../../../components/media/ImageUploadField';
import Button from '../../../components/ui/Button';
import { updateWeddingSubdomain, checkSubdomainAvailability } from '../../../lib/api';

const CmsCoupleSection = ({ content, onChange, weddingId, weddingData, token }) => {
  const couple = content?.couple || {};
  const event = content?.event || {};
  const bank = content?.bank || {};

  // Subdomain state
  const currentSubdomain = weddingData?.custom_domain || '';
  const planId = weddingData?.plan_id || 'free';
  const isProOrHigher = ['pro', 'premium', 'advanced', 'partner'].includes(planId);

  const [subdomainInput, setSubdomainInput] = useState(currentSubdomain);
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [subdomainReason, setSubdomainReason] = useState('');
  const [subdomainSaving, setSubdomainSaving] = useState(false);
  const [subdomainError, setSubdomainError] = useState('');
  const [subdomainSuccess, setSubdomainSuccess] = useState(false);
  const debounceRef = useRef(null);

  // Sync subdomain input when weddingData changes
  useEffect(() => {
    setSubdomainInput(weddingData?.custom_domain || '');
  }, [weddingData?.custom_domain]);

  const subdomainChanged = subdomainInput !== currentSubdomain;

  // Debounced availability check
  const checkAvailability = useCallback(async (value) => {
    if (!value || value.length < 3 || !token || !weddingId) {
      setSubdomainAvailable(null);
      setSubdomainReason('');
      return;
    }
    try {
      const result = await checkSubdomainAvailability(token, weddingId, value);
      setSubdomainAvailable(result.available);
      setSubdomainReason(result.reason || '');
    } catch {
      setSubdomainAvailable(null);
    }
  }, [token, weddingId]);

  const handleSubdomainChange = (value) => {
    const normalized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomainInput(normalized);
    setSubdomainError('');
    setSubdomainSuccess(false);
    setSubdomainAvailable(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (normalized && normalized !== currentSubdomain) {
      debounceRef.current = setTimeout(() => checkAvailability(normalized), 500);
    }
  };

  const handleSaveSubdomain = async () => {
    if (!subdomainChanged || subdomainSaving) return;
    setSubdomainSaving(true);
    setSubdomainError('');
    setSubdomainSuccess(false);
    try {
      await updateWeddingSubdomain(token, weddingId, subdomainInput || null);
      setSubdomainSuccess(true);
      setTimeout(() => setSubdomainSuccess(false), 3000);
    } catch (err) {
      setSubdomainError(err.message);
    } finally {
      setSubdomainSaving(false);
    }
  };

  const updateCouple = (field, value) => {
    onChange({
      ...content,
      couple: { ...couple, [field]: value },
    });
  };

  const updateEvent = (field, value) => {
    onChange({
      ...content,
      event: { ...event, [field]: value },
    });
  };

  const updateBank = (field, value) => {
    onChange({
      ...content,
      bank: { ...bank, [field]: value },
    });
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 shadow-romantic-sm border border-border">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="Heart" size={20} color="var(--color-primary)" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Couple & Event Info</h3>
      </div>

      <div className="space-y-4">
        <Input
          label="Couple Names (display)"
          type="text"
          placeholder="e.g., Sarah & Michael"
          value={couple.names || ''}
          onChange={(e) => updateCouple('names', e.target.value)}
        />

        {/* Wedding URL / Subdomain */}
        {weddingId && (
          <div className="pt-3 pb-1 border-t border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Globe" size={18} color="var(--color-primary)" />
              <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Wedding URL</h4>
              {!isProOrHigher && (
                <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full font-bold uppercase">Pro+</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <input
                  className="w-full bg-background border border-border rounded-lg py-2.5 px-3 pr-32 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="sarah-michael"
                  value={subdomainInput}
                  onChange={(e) => handleSubdomainChange(e.target.value)}
                  disabled={!isProOrHigher}
                  maxLength={50}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">.ourday.my.id</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveSubdomain}
                disabled={!isProOrHigher || !subdomainChanged || subdomainSaving || (subdomainAvailable === false)}
                loading={subdomainSaving}
              >
                Save
              </Button>
            </div>
            {/* Availability feedback */}
            {subdomainAvailable === true && subdomainChanged && (
              <p className="text-xs mt-1 text-emerald-600 flex items-center gap-1">
                <Icon name="Check" size={12} /> Available
              </p>
            )}
            {subdomainAvailable === false && (
              <p className="text-xs mt-1 text-red-600 flex items-center gap-1">
                <Icon name="X" size={12} /> {subdomainReason || 'Already taken'}
              </p>
            )}
            {subdomainError && <p className="text-xs mt-1 text-red-600">{subdomainError}</p>}
            {subdomainSuccess && (
              <p className="text-xs mt-1 text-emerald-600 flex items-center gap-1">
                <Icon name="Check" size={12} /> Subdomain saved!
              </p>
            )}
            {currentSubdomain && (
              <p className="text-xs mt-2 text-muted-foreground">
                Live URL:{' '}
                <a href={`https://${currentSubdomain}.ourday.my.id`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {currentSubdomain}.ourday.my.id
                </a>
              </p>
            )}
            {!isProOrHigher && (
              <p className="text-xs mt-1 text-muted-foreground">Upgrade to Pro or higher to get your own subdomain URL.</p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Person 1 Name"
            type="text"
            placeholder="e.g., Sarah"
            value={couple.person1 || ''}
            onChange={(e) => updateCouple('person1', e.target.value)}
          />
          <Input
            label="Person 2 Name"
            type="text"
            placeholder="e.g., Michael"
            value={couple.person2 || ''}
            onChange={(e) => updateCouple('person2', e.target.value)}
          />
        </div>
        <Input
          label="Wedding Date"
          type="date"
          placeholder="e.g., June 15, 2026"
          value={event.date || ''}
          onChange={(e) => updateEvent('date', e.target.value)}
        />

        {/* Bank & Gift Information */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Landmark" size={18} color="var(--color-primary)" />
            <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Bank & Gift Information</h4>
          </div>
          <div className="space-y-3">
            <Input
              label="Bank Name"
              type="text"
              placeholder="e.g., BCA, Mandiri, BNI"
              value={bank.bankName || ''}
              onChange={(e) => updateBank('bankName', e.target.value)}
            />
            <Input
              label="Account Number"
              type="text"
              placeholder="e.g., 1234567890"
              value={bank.accountNumber || ''}
              onChange={(e) => updateBank('accountNumber', e.target.value)}
            />
            <Input
              label="Account Holder Name"
              type="text"
              placeholder="e.g., John Doe"
              value={bank.accountHolder || ''}
              onChange={(e) => updateBank('accountHolder', e.target.value)}
            />
            <ImageUploadField
              label="Bank QR Code Image"
              value={bank.qrCodeUrl || ''}
              onChange={(e) => updateBank('qrCodeUrl', e.target.value)}
              onUploadSuccess={(url) => updateBank('qrCodeUrl', url)}
              placeholder="Enter image URL or upload"
              showPreview={true}
              weddingId={weddingId}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CmsCoupleSection;
